import * as React from "react";
import { Box, Divider, Grid, Typography, useMediaQuery } from "@mui/material";
import { useTranslation } from "react-i18next";

import ImpactOverTimeByValueChart from "./Charts/ImpactOverTimeByValueChart";
import Circle from "../../../../components/Icons/Circle";

import { StyledItemGraph } from "../../../../components/styles/StyledItemGraph";
import { Bubble } from "../../../../components/Icons/Bubble";
import ImpactOverTimeByEmploymentChart from "./Charts/ImpactOverTimeByEmploymentChart";
import theme from "../../../../components/styles/theme/theme";
import { availableYears } from "../../../../config";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { RootState } from "../../../../store/store";
import { StatisticsData } from "../../../../features/statistics/types";
import { api } from "../../../../api/api";

export const ImpactOverTimeSection: React.FC = () => {
  const isSmScreen = useMediaQuery(theme.breakpoints.down("sm"));
  // todo create generic ChartsSection
  // todo dynamic render statisticsDataArray and data in charts
  return !isSmScreen ? (
    <>
      <Bubble
        style={{
          position: "absolute",
          left: "115px",
          transform: "translateY(15px)",
          zIndex: -1,
        }}
      />
      <Box id="impactOverTime" sx={{ flexGrow: 1 }} padding="140px 25px 50px 25px">
        <Grid item xs={2} sm={2} md={1}>
          <Box sx={{ width: 1 }}>
            <Box display="grid" gridTemplateColumns="repeat(12, 1fr)" gap={2}>
              <Box gridColumn="span 12">
                <StyledItemGraph>
                  <ImpactOverTimeChartSection />
                </StyledItemGraph>
              </Box>
            </Box>
          </Box>
        </Grid>
      </Box>
    </>
  ) : (
    <ImpactOverTimeChartSection />
  );
};

const ImpactOverTimeChartSection: React.FC = () => {
  const { t } = useTranslation();
  const isSmScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const {
    map: { year },
  } = useSelector((state: RootState) => state);
  const [previousYear, setPreviousYear] = useState<string | null>(null);
  const [previousYearStatisticsData, setPreviousYearStatisticsData] =
    useState<StatisticsData | null>(null);

  useEffect(() => {
    const yearIndex = availableYears.indexOf(year);

    if (yearIndex > 0) {
      setPreviousYear(availableYears[yearIndex - 1]);
    } else {
      setPreviousYear(null);
    }
  }, [year]);

  useEffect(() => {
    if (previousYear) {
      api.getStatistics(previousYear).then((data) => {
        if (Array.isArray(data) && data.length > 0) {
          setPreviousYearStatisticsData(data[0]);
        } else {
          setPreviousYearStatisticsData(null);
        }
      });
    } else {
      setPreviousYearStatisticsData(null);
    }
  }, [previousYear]);

  return (
    <Grid
      container
      spacing={{ xs: 2, md: 2 }}
      columns={{ xs: 13, sm: 13, md: 13 }}
      justifyContent="center"
      padding="20px 40px"
    >
      <Grid
        item
        xs={13}
        sm={13}
        md={13}
        paddingBottom={!isSmScreen ? "40px" : "10px"}
      >
        <Typography
          variant="h5"
          component="h5"
          fontWeight="600"
          color={(theme) => theme.palette.secondary.main}
        >
          {t("common.impact_over_time")}
        </Typography>
      </Grid>

      <Grid
        item
        xs={13}
        sm={13}
        md={6}
        textAlign="start"
        paddingBottom={isSmScreen ? "40px" : 0}
      >
        <Typography
          variant="body2"
          component="span"
          fontWeight="600"
          sx={{ fontSize: "16px" }}
        >
          {t("charts.value_added")}
          <Circle
            color={theme.palette.secondary.main}
            width="8px"
            height="8px"
            style={{ margin: "0 8px" }}
          />
        </Typography>
        <Typography
          variant="body2"
          component="span"
          color={theme.palette.primary["500"]}
        >
          {t("charts.by_value_chain_link")}
        </Typography>
        <Typography
          variant="body2"
          component="p"
          color={theme.palette.primary["500"]}
        >
          {t("charts.in_currency", {
            currency: "EUR",
            suffix: "M",
          })}
        </Typography>
        <Box padding={!isSmScreen ? "25px" : 0}>
          <ImpactOverTimeByValueChart
            previousYear={previousYear}
            previousYearStatisticsData={previousYearStatisticsData}
          />
        </Box>
      </Grid>

      {!isSmScreen && (
        <Divider orientation="vertical" flexItem sx={{ margin: "0 auto" }} />
      )}

      <Grid item xs={13} sm={13} md={6} textAlign="start">
        <Typography
          variant="body2"
          component="span"
          fontWeight="600"
          sx={{ fontSize: "16px" }}
        >
          {t("common.employment")}
          <Circle
            color={theme.palette.secondary.main}
            width="8px"
            height="8px"
            style={{ margin: "0 8px" }}
          />
        </Typography>
        <Typography
          variant="body2"
          component="span"
          color={theme.palette.primary["500"]}
        >
          {t("charts.by_value_chain_link")}
        </Typography>
        <Typography
          variant="body2"
          component="p"
          color={theme.palette.primary["500"]}
        >
          {t("charts.in_jobs_factor")}
        </Typography>
        <Box padding={!isSmScreen ? "25px" : 0}>
          <ImpactOverTimeByEmploymentChart
            previousYear={previousYear}
            previousYearStatisticsData={previousYearStatisticsData}
          />
        </Box>
      </Grid>
    </Grid>
  );
};
